import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, IconButton, Button,
  TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert, Snackbar,
  Tooltip, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Divider, Stack, LinearProgress,
  Tabs, Tab, ButtonGroup,
} from '@mui/material';
import {
  Add as AddIcon, Close as CloseIcon, Refresh as RefreshIcon,
  PointOfSale as CashIcon, Lock as LockIcon, LockOpen as LockOpenIcon,
  History as HistoryIcon, ArrowUpward as ArrowUpIcon, ArrowDownward as ArrowDownIcon,
  Delete as DeleteIcon, Print as PrintIcon,
  Receipt as ReceiptIcon, AccountBalanceWallet as WalletIcon,
  PeopleAlt as UsersIcon,
  Assessment as ReportIcon, FileDownload as DownloadIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../../config/environment';
import { usePageBreakpoints, pageContainerSx } from '../../hooks/useClinicPageBreakpoints';
import { useAuth } from '../../contexts/AuthContext';

/* ═══════════════════════════════════════════════════════════════
   CONSTANTES
   ═══════════════════════════════════════════════════════════════ */

const MOVEMENT_TYPES = [
  { value: 'in', label: 'Entrada', positive: true },
  { value: 'out', label: 'Saída', positive: false },
  { value: 'sangria', label: 'Sangria', positive: false },
  { value: 'suprimento', label: 'Suprimento', positive: true },
];

const MOVEMENT_TYPE_LABELS: Record<string, string> = MOVEMENT_TYPES.reduce((acc, t) => {
  acc[t.value] = t.label;
  return acc;
}, {} as Record<string, string>);

const PAYMENT_METHODS = [
  { value: 'dinheiro', label: 'Dinheiro' },
  { value: 'pix', label: 'PIX' },
  { value: 'cartao_credito', label: 'Cartão de Crédito' },
  { value: 'cartao_debito', label: 'Cartão de Débito' },
  { value: 'boleto', label: 'Boleto' },
  { value: 'transferencia', label: 'Transferência' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'convenio', label: 'Convênio' },
  { value: 'outros', label: 'Outros' },
];

/** Aliases para normalizar valores salvos em inglês vindos de outras telas. */
const PAYMENT_METHOD_ALIAS: Record<string, string> = {
  cash: 'dinheiro',
  credit_card: 'cartao_credito',
  debit_card: 'cartao_debito',
  bank_transfer: 'transferencia',
  check: 'cheque',
  insurance: 'convenio',
};

function normalizeMethod(value?: string | null): string {
  if (!value) return 'outros';
  const v = String(value).trim().toLowerCase();
  return PAYMENT_METHOD_ALIAS[v] || v;
}

function paymentMethodLabel(value?: string | null): string {
  const normalized = normalizeMethod(value);
  return PAYMENT_METHODS.find(p => p.value === normalized)?.label || value || '—';
}

const isPositiveMovement = (type: string) =>
  MOVEMENT_TYPES.find(t => t.value === type)?.positive ?? false;

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value) || 0);

const CASH_PRINT_CSS = `
  @page { size: A4; margin: 15mm 18mm; }
  * { box-sizing: border-box; }
  body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 11.5px; color: #1e293b; margin: 0; background: #fff; }

  .doc-header { display: flex; align-items: flex-start; gap: 12px; border-bottom: 3px solid #3b82f6; padding-bottom: 10px; margin-bottom: 14px; }
  .logo { width: 52px; height: 52px; object-fit: contain; flex-shrink: 0; }
  .doc-header-info { flex: 1; }
  .doc-title { font-size: 17px; font-weight: 800; color: #1d4ed8; }
  .doc-sub { font-size: 11px; color: #475569; margin-top: 2px; }
  .doc-header-right { text-align: right; min-width: 140px; }
  .doc-generated { font-size: 10px; color: #94a3b8; margin-top: 4px; }

  .badge { display: inline-block; padding: 3px 10px; border-radius: 99px; font-size: 11px; font-weight: 700; }
  .badge-open { background: #dcfce7; color: #15803d; }
  .badge-closed { background: #f1f5f9; color: #475569; }

  .info-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px 12px; margin: 10px 0; font-size: 11px; }
  .info-item { display: flex; flex-direction: column; }
  .info-label { font-size: 9.5px; text-transform: uppercase; letter-spacing: .05em; color: #94a3b8; font-weight: 600; }
  .info-val { font-weight: 600; color: #1e293b; margin-top: 1px; }

  .kpi-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin: 12px 0 16px; }
  .kpi-box { border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px 12px; background: #f8fafc; }
  .kpi-lbl { font-size: 9.5px; color: #64748b; font-weight: 600; text-transform: uppercase; letter-spacing: .05em; }
  .kpi-val { font-size: 15px; font-weight: 800; color: #1e293b; margin-top: 3px; }

  .sec-title { font-size: 12px; font-weight: 700; color: #2563eb; padding: 6px 0 5px; border-bottom: 2px solid #dbeafe; margin: 16px 0 8px; display: flex; gap: 8px; align-items: baseline; }

  table { width: 100%; border-collapse: collapse; font-size: 11px; }
  thead tr { background: #eff6ff; }
  th { padding: 6px 8px; font-weight: 700; color: #374151; border: 1px solid #bfdbfe; font-size: 10px; text-transform: uppercase; letter-spacing: .04em; }
  td { padding: 5px 8px; border: 1px solid #e5e7eb; vertical-align: top; }
  tbody tr:nth-child(even) { background: #fafafa; }

  .r { text-align: right; }
  .c { text-align: center; }
  .bold { font-weight: 700; }
  .muted { color: #94a3b8; font-size: 10px; }
  .pos { color: #059669; font-weight: 600; }
  .neg { color: #dc2626; font-weight: 600; }
  .pill { display: inline-block; padding: 1px 7px; border-radius: 99px; font-size: 9.5px; font-weight: 700; }
  .pill-green { background: #dcfce7; color: #15803d; }
  .pill-red { background: #fee2e2; color: #b91c1c; }
  .pill-open { background: #dcfce7; color: #15803d; }
  .pill-closed { background: #f1f5f9; color: #475569; }

  .notes { padding: 8px 12px; background: #fafafa; border: 1px solid #e5e7eb; border-radius: 6px; font-size: 11px; white-space: pre-wrap; }
  .page-break { page-break-before: always; }
  .doc-footer { margin-top: 24px; padding-top: 8px; border-top: 1px dashed #cbd5e1; font-size: 9px; color: #94a3b8; display: flex; justify-content: space-between; }

  @media print {
    .page-break { page-break-before: always; }
    table { font-size: 10px; }
  }
`;

const formatDateTime = (d?: string | null) =>
  d ? new Date(d).toLocaleString('pt-BR') : '—';

const formatDate = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString('pt-BR') : '—';

/* ── Períodos / utils ── */

function todayIso(): string {
  return new Date().toISOString().split('T')[0];
}

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().split('T')[0];
}

function firstDayOfMonthIso(offsetMonths = 0): string {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offsetMonths);
  return d.toISOString().split('T')[0];
}

function lastDayOfMonthIso(offsetMonths = 0): string {
  const d = new Date();
  d.setMonth(d.getMonth() + offsetMonths + 1, 0);
  return d.toISOString().split('T')[0];
}

type PeriodReport = {
  filters: { date_from: string | null; date_to: string | null; status: string; user_id: string | null; unit_id: string | null };
  totals: {
    registers: number; registers_open: number; registers_closed: number;
    total_opening: number; total_in: number; total_out: number;
    total_expected: number; total_closing: number; total_difference: number;
    movements_count: number;
  };
  by_method: Record<string, { in: number; out: number; net: number; count: number }>;
  by_movement_type: Record<string, { count: number; total: number }>;
  by_user: Array<{ user_id: string; user_name: string | null; registers: number; in: number; out: number; net: number }>;
  sessions: Array<Record<string, any>>;
  movements?: Array<Record<string, any>>;
};

function downloadCsv(filename: string, rows: (string | number | null | undefined)[][]) {
  const escape = (v: string | number | null | undefined) => {
    if (v == null) return '';
    const s = String(v).replace(/"/g, '""');
    return /[",;\n\r]/.test(s) ? `"${s}"` : s;
  };
  const csv = rows.map((r) => r.map(escape).join(';')).join('\r\n');
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/* ═══════════════════════════════════════════════════════════════
   COMPONENTE
   ═══════════════════════════════════════════════════════════════ */

const CashRegister: React.FC = () => {
  const { isMobile } = usePageBreakpoints();
  const { user } = useAuth();
  // Apenas admin_master pode ver caixa de outros operadores.
  const isAdminMaster = user?.role === 'admin_master';

  const [tab, setTab] = useState<'session' | 'report'>('session');

  const [currentRegister, setCurrentRegister] = useState<any>(null);
  const [movements, setMovements] = useState<any[]>([]);
  const [history, setHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Period report
  const [report, setReport] = useState<PeriodReport | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState<string>(firstDayOfMonthIso());
  const [dateTo, setDateTo] = useState<string>(todayIso());
  const [statusFilter, setStatusFilter] = useState<'all' | 'open' | 'closed'>('all');
  const [userFilter, setUserFilter] = useState<string>('all'); // 'all' or user_id
  const [unitRegistrars, setUnitRegistrars] = useState<{ id: string; full_name: string }[]>([]);

  // Filters for movements table
  const [movFilterType, setMovFilterType] = useState<string>('');
  const [movFilterMethod, setMovFilterMethod] = useState<string>('');
  const [movSearch, setMovSearch] = useState<string>('');

  // Open dialog
  const [openDialogVisible, setOpenDialogVisible] = useState(false);
  const [openingBalance, setOpeningBalance] = useState<number>(0);
  const [openingNotes, setOpeningNotes] = useState('');
  const [openingRegister, setOpeningRegister] = useState(false);

  // Movement dialog
  const [movementDialogVisible, setMovementDialogVisible] = useState(false);
  const [movementForm, setMovementForm] = useState({
    movement_type: 'in',
    amount: '' as number | '',
    description: '',
    payment_method: 'dinheiro',
    reference: '',
  });
  const [savingMovement, setSavingMovement] = useState(false);

  // Close dialog
  const [closeDialogVisible, setCloseDialogVisible] = useState(false);
  const [closingBalance, setClosingBalance] = useState<number>(0);
  const [closingNotes, setClosingNotes] = useState('');
  const [closingRegister, setClosingRegister] = useState(false);

  // Delete movement dialog
  const [deleteMovementId, setDeleteMovementId] = useState<string | null>(null);
  const [deletingMovement, setDeletingMovement] = useState(false);

  // Detail dialog (history)
  const [detailRegisterId, setDetailRegisterId] = useState<string | null>(null);
  const [detailRegister, setDetailRegister] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'info' });

  const showSnack = (message: string, severity: 'success' | 'error' | 'info' = 'success') =>
    setSnackbar({ open: true, message, severity });

  /* ─── Fetch ─── */

  const fetchCurrentRegister = useCallback(async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      const res = await axios.get(getApiUrl('/financial-module/cash-register/current'), {
        params: { user_id: user.id },
      });
      const data = res.data?.data || res.data;
      if (data && data.id) {
        setCurrentRegister(data);
        setMovements(data.movements || []);
      } else {
        setCurrentRegister(null);
        setMovements([]);
      }
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setCurrentRegister(null);
        setMovements([]);
      } else {
        console.error('Erro ao buscar caixa atual:', err);
        showSnack('Erro ao buscar caixa', 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  const fetchHistory = useCallback(async () => {
    if (!user?.id) return;
    try {
      setHistoryLoading(true);
      const res = await axios.get(getApiUrl('/financial-module/cash-register/history'), {
        params: { user_id: user.id },
      });
      setHistory(res.data?.data || []);
    } catch (err) {
      console.error('Erro ao buscar histórico:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { fetchCurrentRegister(); fetchHistory(); }, [fetchCurrentRegister, fetchHistory]);

  const fetchReport = useCallback(async () => {
    try {
      setReportLoading(true);
      setReportError(null);
      const params: Record<string, string> = {
        date_from: dateFrom,
        date_to: dateTo,
        status: statusFilter,
        include_movements: 'true',
      };
      if (isAdminMaster) {
        params.user_id = userFilter && userFilter !== 'all' ? userFilter : 'all';
      } else if (user?.id) {
        params.user_id = user.id;
      }
      const res = await axios.get(getApiUrl('/financial-module/cash-register/period-report'), { params });
      setReport(res.data || null);
    } catch (err: any) {
      console.error('Erro ao gerar relatório:', err);
      const msg = err?.response?.data?.error || err?.message || 'Erro ao gerar relatório';
      setReportError(typeof msg === 'string' ? msg : 'Erro ao gerar relatório');
      setReport(null);
    } finally {
      setReportLoading(false);
    }
  }, [dateFrom, dateTo, statusFilter, userFilter, isAdminMaster, user?.id]);

  useEffect(() => {
    if (tab !== 'report') return;
    fetchReport();
  }, [tab, fetchReport]);

  useEffect(() => {
    if (!isAdminMaster) return;
    let active = true;
    (async () => {
      try {
        const res = await axios.get(getApiUrl('/clinic/reports/unit-registrars'));
        if (!active) return;
        const list = (res.data?.data || []) as { id: string; full_name: string }[];
        setUnitRegistrars(list);
      } catch (_e) {
        if (active) setUnitRegistrars([]);
      }
    })();
    return () => { active = false; };
  }, [isAdminMaster]);

  useEffect(() => {
    if (loading) return;
    if (currentRegister) return;
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('open') === '1') {
        setOpenDialogVisible(true);
        params.delete('open');
        const newQuery = params.toString();
        const newUrl = window.location.pathname + (newQuery ? `?${newQuery}` : '') + window.location.hash;
        window.history.replaceState({}, '', newUrl);
      }
    } catch (_e) {
      // ignore
    }
  }, [loading, currentRegister]);

  /* ─── Summary ─── */

  const summary = useMemo(() => {
    if (!currentRegister) return null;
    let totalIn = 0;
    let totalOut = 0;
    const byMethod: Record<string, { in: number; out: number; net: number; count: number }> = {};
    for (const m of movements || []) {
      const amt = Number(m.amount) || 0;
      const method = normalizeMethod(m.payment_method);
      if (!byMethod[method]) byMethod[method] = { in: 0, out: 0, net: 0, count: 0 };
      byMethod[method].count += 1;
      if (isPositiveMovement(m.movement_type)) {
        totalIn += amt;
        byMethod[method].in += amt;
        byMethod[method].net += amt;
      } else {
        totalOut += amt;
        byMethod[method].out += amt;
        byMethod[method].net -= amt;
      }
    }
    const opening = Number(currentRegister.opening_balance) || 0;
    const expectedBalance = opening + totalIn - totalOut;
    return { totalIn, totalOut, expectedBalance, opening, byMethod };
  }, [movements, currentRegister]);

  const filteredMovements = useMemo(() => {
    const term = movSearch.trim().toLowerCase();
    return (movements || []).filter(m => {
      if (movFilterType && m.movement_type !== movFilterType) return false;
      if (movFilterMethod && normalizeMethod(m.payment_method) !== movFilterMethod) return false;
      if (term) {
        const hay = `${m.description || ''} ${m.reference || ''} ${m.patient_name || ''}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [movements, movFilterType, movFilterMethod, movSearch]);

  /* ─── Actions ─── */

  const handleOpenRegister = async () => {
    const value = Number(openingBalance);
    if (isNaN(value) || value < 0) {
      showSnack('Saldo de abertura inválido', 'error');
      return;
    }
    try {
      setOpeningRegister(true);
      await axios.post(getApiUrl('/financial-module/cash-register/open'), {
        opening_balance: value,
        notes: openingNotes || undefined,
        user_id: user?.id,
        user_name: user?.full_name,
      });
      showSnack('Caixa aberto com sucesso');
      setOpenDialogVisible(false);
      setOpeningBalance(0);
      setOpeningNotes('');
      fetchCurrentRegister();
      fetchHistory();
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || 'Erro ao abrir caixa';
      showSnack(typeof msg === 'string' ? msg : 'Erro ao abrir caixa', 'error');
    } finally {
      setOpeningRegister(false);
    }
  };

  const handleAddMovement = async () => {
    const amountNumber = typeof movementForm.amount === 'string'
      ? parseFloat(movementForm.amount)
      : movementForm.amount;
    if (!amountNumber || isNaN(amountNumber) || amountNumber <= 0) {
      showSnack('Informe um valor válido', 'error');
      return;
    }
    if (!movementForm.description.trim()) {
      showSnack('Descrição é obrigatória', 'error');
      return;
    }
    try {
      setSavingMovement(true);
      await axios.post(getApiUrl('/financial-module/cash-register/movements'), {
        cash_register_id: currentRegister.id,
        movement_type: movementForm.movement_type,
        amount: amountNumber,
        description: movementForm.description.trim(),
        payment_method: movementForm.payment_method,
        reference: movementForm.reference || undefined,
        user_id: user?.id,
        user_name: user?.full_name,
      });
      showSnack('Movimento adicionado');
      setMovementDialogVisible(false);
      setMovementForm({ movement_type: 'in', amount: '', description: '', payment_method: 'dinheiro', reference: '' });
      fetchCurrentRegister();
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || 'Erro ao adicionar movimento';
      showSnack(typeof msg === 'string' ? msg : 'Erro ao adicionar movimento', 'error');
    } finally {
      setSavingMovement(false);
    }
  };

  const handleRemoveMovement = async () => {
    if (!deleteMovementId) return;
    try {
      setDeletingMovement(true);
      await axios.delete(getApiUrl(`/financial-module/cash-register/movements/${deleteMovementId}`), {
        data: { user_id: user?.id, user_name: user?.full_name },
      });
      showSnack('Movimento removido');
      setDeleteMovementId(null);
      fetchCurrentRegister();
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || 'Erro ao remover movimento';
      showSnack(typeof msg === 'string' ? msg : 'Erro ao remover movimento', 'error');
    } finally {
      setDeletingMovement(false);
    }
  };

  const handleCloseRegister = async () => {
    try {
      setClosingRegister(true);
      await axios.post(getApiUrl(`/financial-module/cash-register/${currentRegister.id}/close`), {
        closing_balance: closingBalance,
        notes: closingNotes,
        user_id: user?.id,
        user_name: user?.full_name,
      });
      showSnack('Caixa fechado com sucesso');
      setCloseDialogVisible(false);
      setClosingBalance(0);
      setClosingNotes('');
      fetchCurrentRegister();
      fetchHistory();
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || 'Erro ao fechar caixa';
      showSnack(typeof msg === 'string' ? msg : 'Erro ao fechar caixa', 'error');
    } finally {
      setClosingRegister(false);
    }
  };

  const handleOpenDetails = async (id: string) => {
    setDetailRegisterId(id);
    setDetailLoading(true);
    try {
      const res = await axios.get(getApiUrl(`/financial-module/cash-register/${id}`));
      setDetailRegister(res.data?.data || null);
    } catch (err) {
      console.error('Erro ao buscar detalhes do caixa:', err);
      showSnack('Erro ao buscar detalhes do caixa', 'error');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetailDialog = () => {
    setDetailRegisterId(null);
    setDetailRegister(null);
  };

  const printRegister = (register: any) => {
    if (!register) return;
    const fromCurrent = register.id === currentRegister?.id ? summary : null;
    const totals = fromCurrent ?? {
      totalIn: Number(register.total_in) || 0,
      totalOut: Number(register.total_out) || 0,
      expectedBalance: Number(register.expected_balance) || 0,
      opening: Number(register.opening_balance) || 0,
      byMethod: (register.by_method || {}) as Record<string, { in: number; out: number; net: number; count: number }>,
    };
    const w = window.open('', '_blank', 'width=820,height=960');
    if (!w) return;
    const movs = (register.movements || []) as any[];
    const statusLabel = register.status === 'closed' ? 'Fechado' : 'Aberto';
    const diffColor = register.difference == null || register.difference === 0 ? '#059669' : Number(register.difference) > 0 ? '#2563eb' : '#dc2626';
    const byMethodRows = Object.entries(totals.byMethod || {})
      .sort(([, a]: any, [, b]: any) => Math.abs(b.net) - Math.abs(a.net))
      .map(([m, v]: any) =>
        `<tr><td>${paymentMethodLabel(m)}</td>
          <td class="r pos">${formatCurrency(v.in)}</td>
          <td class="r neg">${formatCurrency(v.out)}</td>
          <td class="r bold">${formatCurrency(v.net)}</td>
          <td class="r muted">${v.count ?? '—'}</td></tr>`
      ).join('') || '<tr><td colspan="5" class="c muted">Sem movimentações por método</td></tr>';
    const movRows = movs.map((m: any) => {
      const pos = isPositiveMovement(m.movement_type);
      return `<tr>
        <td class="muted">${formatDateTime(m.created_at)}</td>
        <td><span class="pill ${pos ? 'pill-green' : 'pill-red'}">${MOVEMENT_TYPE_LABELS[m.movement_type] || m.movement_type}</span></td>
        <td>${String(m.patient_name || '—').replace(/</g, '&lt;')}</td>
        <td class="muted">${String(m.description || '').replace(/</g, '&lt;')}</td>
        <td>${paymentMethodLabel(m.payment_method)}</td>
        <td class="r bold ${pos ? 'pos' : 'neg'}">${pos ? '+' : '-'} ${formatCurrency(m.amount)}</td>
      </tr>`;
    }).join('') || '<tr><td colspan="6" class="c muted">Sem movimentações registradas</td></tr>';

    w.document.write(`<!doctype html><html lang="pt-BR"><head><meta charset="utf-8">
      <title>Caixa — ${formatDateTime(register.open_date || register.created_at)}</title>
      <style>${CASH_PRINT_CSS}</style></head><body>

      <div class="doc-header">
        <img class="logo" src="/principal-logo.png" alt="Logo" onerror="this.style.display='none'" />
        <div class="doc-header-info">
          <div class="doc-title">Comprovante de Caixa</div>
          <div class="doc-sub">Sessão: ${formatDateTime(register.open_date || register.opened_at || register.created_at)}</div>
        </div>
        <div class="doc-header-right">
          <div class="badge ${register.status === 'closed' ? 'badge-closed' : 'badge-open'}">${statusLabel}</div>
          <div class="doc-generated">Gerado em ${new Date().toLocaleString('pt-BR')}</div>
        </div>
      </div>

      <div class="info-grid">
        <div class="info-item"><span class="info-label">Aberto por</span><span class="info-val">${register.opened_by_name || register.opened_by_user?.full_name || '—'}</span></div>
        <div class="info-item"><span class="info-label">Fechado por</span><span class="info-val">${register.closed_by_name || register.closed_by_user?.full_name || '—'}</span></div>
        <div class="info-item"><span class="info-label">Data de abertura</span><span class="info-val">${formatDateTime(register.open_date || register.opened_at || register.created_at)}</span></div>
        <div class="info-item"><span class="info-label">Data de fechamento</span><span class="info-val">${formatDateTime(register.close_date || register.closed_at)}</span></div>
      </div>

      <div class="kpi-row">
        <div class="kpi-box"><div class="kpi-lbl">Saldo de abertura</div><div class="kpi-val">${formatCurrency(totals.opening)}</div></div>
        <div class="kpi-box"><div class="kpi-lbl">Entradas</div><div class="kpi-val pos">${formatCurrency(totals.totalIn)}</div></div>
        <div class="kpi-box"><div class="kpi-lbl">Saídas</div><div class="kpi-val neg">${formatCurrency(totals.totalOut)}</div></div>
        <div class="kpi-box"><div class="kpi-lbl">Saldo esperado</div><div class="kpi-val bold">${formatCurrency(totals.expectedBalance)}</div></div>
        ${register.closing_balance != null ? `<div class="kpi-box"><div class="kpi-lbl">Saldo fechamento</div><div class="kpi-val">${formatCurrency(register.closing_balance)}</div></div>` : ''}
        ${register.difference != null ? `<div class="kpi-box"><div class="kpi-lbl">Diferença</div><div class="kpi-val" style="color:${diffColor}">${formatCurrency(register.difference)}</div></div>` : ''}
      </div>

      <div class="sec-title">Por Forma de Pagamento</div>
      <table><thead><tr><th>Método</th><th class="r">Entradas</th><th class="r">Saídas</th><th class="r">Líquido</th><th class="r">Qtd.</th></tr></thead>
      <tbody>${byMethodRows}</tbody></table>

      <div class="sec-title">Movimentações <span class="muted">(${movs.length} registro${movs.length !== 1 ? 's' : ''})</span></div>
      <table><thead><tr><th>Data/Hora</th><th>Tipo</th><th>Paciente</th><th>Descrição</th><th>Método</th><th class="r">Valor</th></tr></thead>
      <tbody>${movRows}</tbody></table>

      ${register.notes ? `<div class="sec-title">Observações</div><div class="notes">${String(register.notes).replace(/</g,'&lt;')}</div>` : ''}

      <div class="doc-footer">
        <span>Comprovante de Caixa</span>
        <span>Gerado em ${new Date().toLocaleString('pt-BR')}</span>
      </div>
      <script>window.onload=()=>setTimeout(()=>window.print(),280)</script>
      </body></html>`);
    w.document.close();
  };

  /* ─── Report helpers ─── */

  const periodLabel = useMemo(() => {
    const a = dateFrom ? new Date(dateFrom + 'T00:00:00').toLocaleDateString('pt-BR') : '—';
    const b = dateTo ? new Date(dateTo + 'T00:00:00').toLocaleDateString('pt-BR') : '—';
    return `${a} a ${b}`;
  }, [dateFrom, dateTo]);

  const applyPreset = (preset: 'today' | 'yesterday' | '7d' | '30d' | 'month' | 'last_month') => {
    if (preset === 'today') {
      const t = todayIso();
      setDateFrom(t); setDateTo(t);
    } else if (preset === 'yesterday') {
      const y = isoDaysAgo(1);
      setDateFrom(y); setDateTo(y);
    } else if (preset === '7d') {
      setDateFrom(isoDaysAgo(6)); setDateTo(todayIso());
    } else if (preset === '30d') {
      setDateFrom(isoDaysAgo(29)); setDateTo(todayIso());
    } else if (preset === 'month') {
      setDateFrom(firstDayOfMonthIso()); setDateTo(todayIso());
    } else if (preset === 'last_month') {
      setDateFrom(firstDayOfMonthIso(-1)); setDateTo(lastDayOfMonthIso(-1));
    }
  };

  const exportPeriodCsv = () => {
    if (!report) return;
    const rows: (string | number)[][] = [];
    rows.push(['Relatório de Caixa — Período', periodLabel]);
    rows.push([]);
    rows.push(['Caixas', report.totals.registers]);
    rows.push(['Caixas abertos', report.totals.registers_open]);
    rows.push(['Caixas fechados', report.totals.registers_closed]);
    rows.push(['Total abertura', report.totals.total_opening.toFixed(2)]);
    rows.push(['Entradas', report.totals.total_in.toFixed(2)]);
    rows.push(['Saídas', report.totals.total_out.toFixed(2)]);
    rows.push(['Saldo esperado', report.totals.total_expected.toFixed(2)]);
    rows.push(['Saldo fechamento', report.totals.total_closing.toFixed(2)]);
    rows.push(['Diferença', report.totals.total_difference.toFixed(2)]);
    rows.push([]);
    rows.push(['Forma de pagamento', 'Entradas', 'Saídas', 'Líquido', 'Qtd.']);
    for (const [m, v] of Object.entries(report.by_method)) {
      rows.push([paymentMethodLabel(m), v.in.toFixed(2), v.out.toFixed(2), v.net.toFixed(2), v.count]);
    }
    rows.push([]);
    rows.push(['Operador', 'Sessões', 'Entradas', 'Saídas', 'Líquido']);
    for (const u of report.by_user) {
      rows.push([u.user_name || '—', u.registers, u.in.toFixed(2), u.out.toFixed(2), u.net.toFixed(2)]);
    }
    rows.push([]);
    rows.push(['Sessões']);
    rows.push(['Abertura', 'Fechamento', 'Operador', 'Fechado por', 'Status', 'Abertura R$', 'Entradas', 'Saídas', 'Esperado', 'Fechamento', 'Diferença']);
    for (const s of report.sessions) {
      rows.push([
        formatDateTime(s.open_date),
        formatDateTime(s.close_date),
        s.opened_by_name || '—',
        s.closed_by_name || '—',
        s.status === 'closed' ? 'Fechado' : 'Aberto',
        Number(s.opening_balance || 0).toFixed(2),
        Number(s.total_in || 0).toFixed(2),
        Number(s.total_out || 0).toFixed(2),
        Number(s.expected_balance || 0).toFixed(2),
        s.closing_balance != null ? Number(s.closing_balance).toFixed(2) : '',
        s.difference != null ? Number(s.difference).toFixed(2) : '',
      ]);
    }
    if (report.movements && report.movements.length) {
      rows.push([]);
      rows.push(['Movimentações']);
      rows.push(['Data', 'Tipo', 'Paciente', 'Descrição', 'Método', 'Valor']);
      for (const m of report.movements) {
        rows.push([
          formatDateTime(m.created_at),
          MOVEMENT_TYPE_LABELS[m.movement_type] || m.movement_type,
          m.patient_name || '—',
          m.description || '',
          paymentMethodLabel(m.payment_method),
          (isPositiveMovement(m.movement_type) ? 1 : -1) * Number(m.amount || 0),
        ]);
      }
    }
    downloadCsv(`caixa_${dateFrom}_a_${dateTo}.csv`, rows);
  };

  const printPeriodReport = () => {
    if (!report) return;
    const w = window.open('', '_blank', 'width=920,height=1040');
    if (!w) return;
    const t = report.totals;
    const diffColor = t.total_difference === 0 ? '#059669' : t.total_difference > 0 ? '#2563eb' : '#dc2626';

    const userScopeLabel = !isAdminMaster
      ? (user?.full_name || 'Meu caixa')
      : userFilter === 'all' || !userFilter
      ? 'Todos os operadores'
      : unitRegistrars.find((u) => u.id === userFilter)?.full_name || 'Operador';

    const statusScopeLabel = statusFilter === 'all' ? 'Todos' : statusFilter === 'open' ? 'Abertos' : 'Fechados';

    const byMethodRows = Object.entries(report.by_method)
      .sort((a, b) => Math.abs(b[1].net) - Math.abs(a[1].net))
      .map(([m, v]) =>
        `<tr><td><strong>${paymentMethodLabel(m)}</strong></td>
          <td class="r pos">${formatCurrency(v.in)}</td>
          <td class="r neg">${formatCurrency(v.out)}</td>
          <td class="r bold">${formatCurrency(v.net)}</td>
          <td class="r muted">${v.count}</td></tr>`,
      ).join('') || '<tr><td colspan="5" class="c muted">Sem dados</td></tr>';

    const byUserRows = report.by_user.map((u) =>
      `<tr><td><strong>${u.user_name || '—'}</strong></td>
        <td class="r">${u.registers}</td>
        <td class="r pos">${formatCurrency(u.in)}</td>
        <td class="r neg">${formatCurrency(u.out)}</td>
        <td class="r bold">${formatCurrency(u.net)}</td></tr>`,
    ).join('') || '<tr><td colspan="5" class="c muted">Sem dados</td></tr>';

    const sessionRows = report.sessions.map((s) => {
      const diff = s.difference != null ? Number(s.difference) : null;
      const diffStyle = diff == null ? '' : diff === 0 ? 'color:#059669' : diff > 0 ? 'color:#2563eb' : 'color:#dc2626';
      return `<tr>
        <td class="muted">${formatDateTime(s.open_date)}</td>
        <td class="muted">${formatDateTime(s.close_date) || '—'}</td>
        <td><strong>${s.opened_by_name || '—'}</strong></td>
        <td><span class="pill ${s.status === 'closed' ? 'pill-closed' : 'pill-open'}">${s.status === 'closed' ? 'Fechado' : 'Aberto'}</span></td>
        <td class="r">${formatCurrency(s.opening_balance)}</td>
        <td class="r pos">${formatCurrency(s.total_in)}</td>
        <td class="r neg">${formatCurrency(s.total_out)}</td>
        <td class="r bold">${formatCurrency(s.expected_balance)}</td>
        <td class="r">${s.closing_balance != null ? formatCurrency(s.closing_balance) : '—'}</td>
        <td class="r bold" style="${diffStyle}">${diff != null ? formatCurrency(diff) : '—'}</td>
      </tr>`;
    }).join('') || '<tr><td colspan="10" class="c muted">Sem sessões no período</td></tr>';

    const movRows = (report.movements || []).map((m: any) => {
      const pos = isPositiveMovement(m.movement_type);
      return `<tr>
        <td class="muted">${formatDateTime(m.created_at)}</td>
        <td><span class="pill ${pos ? 'pill-green' : 'pill-red'}">${MOVEMENT_TYPE_LABELS[m.movement_type] || m.movement_type}</span></td>
        <td>${String(m.patient_name || '—').replace(/</g, '&lt;')}</td>
        <td class="muted">${String(m.description || '').replace(/</g, '&lt;')}</td>
        <td>${paymentMethodLabel(m.payment_method)}</td>
        <td class="r bold ${pos ? 'pos' : 'neg'}">${pos ? '+' : '-'} ${formatCurrency(m.amount)}</td>
      </tr>`;
    }).join('') || '';

    w.document.write(`<!doctype html><html lang="pt-BR"><head><meta charset="utf-8">
      <title>Relatório de Caixa — ${dateFrom} a ${dateTo}</title>
      <style>${CASH_PRINT_CSS}</style></head><body>

      <div class="doc-header">
        <img class="logo" src="/principal-logo.png" alt="Logo" onerror="this.style.display='none'" />
        <div class="doc-header-info">
          <div class="doc-title">Relatório de Caixa por Período</div>
          <div class="doc-sub">Período: <strong>${periodLabel}</strong></div>
          <div class="doc-sub">Operador: <strong>${String(userScopeLabel).replace(/</g,'&lt;')}</strong> &nbsp;·&nbsp; Status: <strong>${statusScopeLabel}</strong></div>
        </div>
        <div class="doc-header-right">
          <div class="doc-generated">Gerado em ${new Date().toLocaleString('pt-BR')}</div>
        </div>
      </div>

      <div class="kpi-row" style="grid-template-columns:repeat(4,1fr)">
        <div class="kpi-box"><div class="kpi-lbl">Sessões de caixa</div><div class="kpi-val">${t.registers} <span class="muted" style="font-size:12px">(${t.registers_open} aberto · ${t.registers_closed} fechado)</span></div></div>
        <div class="kpi-box"><div class="kpi-lbl">Saldo de abertura</div><div class="kpi-val">${formatCurrency(t.total_opening)}</div></div>
        <div class="kpi-box"><div class="kpi-lbl">Total de entradas</div><div class="kpi-val pos">${formatCurrency(t.total_in)}</div></div>
        <div class="kpi-box"><div class="kpi-lbl">Total de saídas</div><div class="kpi-val neg">${formatCurrency(t.total_out)}</div></div>
        <div class="kpi-box"><div class="kpi-lbl">Saldo esperado</div><div class="kpi-val bold">${formatCurrency(t.total_expected)}</div></div>
        <div class="kpi-box"><div class="kpi-lbl">Saldo de fechamento</div><div class="kpi-val">${formatCurrency(t.total_closing)}</div></div>
        <div class="kpi-box"><div class="kpi-lbl">Diferença</div><div class="kpi-val bold" style="color:${diffColor}">${formatCurrency(t.total_difference)}</div></div>
        <div class="kpi-box"><div class="kpi-lbl">Movimentações</div><div class="kpi-val">${t.movements_count}</div></div>
      </div>

      <div class="sec-title">Por Forma de Pagamento</div>
      <table><thead><tr><th>Método</th><th class="r">Entradas</th><th class="r">Saídas</th><th class="r">Líquido</th><th class="r">Qtd.</th></tr></thead>
      <tbody>${byMethodRows}</tbody></table>

      ${report.by_user.length > 1 || isAdminMaster ? `
        <div class="sec-title">Por Operador</div>
        <table><thead><tr><th>Operador</th><th class="r">Sessões</th><th class="r">Entradas</th><th class="r">Saídas</th><th class="r">Líquido</th></tr></thead>
        <tbody>${byUserRows}</tbody></table>` : ''}

      <div class="sec-title">Sessões do Período <span class="muted">(${report.sessions.length})</span></div>
      <table><thead><tr>
        <th>Abertura</th><th>Fechamento</th><th>Operador</th><th>Status</th>
        <th class="r">Abertura R$</th><th class="r">Entradas</th><th class="r">Saídas</th>
        <th class="r">Esperado</th><th class="r">Fechamento</th><th class="r">Diferença</th>
      </tr></thead>
      <tbody>${sessionRows}</tbody></table>

      ${report.movements && report.movements.length ? `
        <div class="sec-title page-break">Movimentações <span class="muted">(${report.movements.length})</span></div>
        <table><thead><tr><th>Data/Hora</th><th>Tipo</th><th>Paciente</th><th>Descrição</th><th>Método</th><th class="r">Valor</th></tr></thead>
        <tbody>${movRows}</tbody></table>` : ''}

      <div class="doc-footer">
        <span>Relatório de Caixa — ${periodLabel}</span>
        <span>Gerado em ${new Date().toLocaleString('pt-BR')}</span>
      </div>
      <script>window.onload=()=>setTimeout(()=>window.print(),320)</script>
      </body></html>`);
    w.document.close();
  };

  /* ─── Render ─── */

  const openedByName = currentRegister?.opened_by_user?.full_name || currentRegister?.opened_by_name || '—';
  const sessionStart = currentRegister?.open_date || currentRegister?.opened_at || currentRegister?.created_at;

  return (
    <Box sx={pageContainerSx}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CashIcon sx={{ fontSize: 32, color: '#3b82f6' }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Meu Caixa</Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.full_name ? `Operador: ${user.full_name} · ` : ''}
              Cada usuário tem o próprio caixa — abertura, movimentos e fechamento
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={1}>
          {currentRegister && (
            <Tooltip title="Imprimir resumo">
              <IconButton onClick={() => printRegister({ ...currentRegister, movements })}>
                <PrintIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Atualizar">
            <IconButton onClick={() => { fetchCurrentRegister(); fetchHistory(); }}><RefreshIcon /></IconButton>
          </Tooltip>
        </Stack>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 2, borderBottom: '1px solid #e5e7eb' }}
      >
        <Tab value="session" icon={<WalletIcon fontSize="small" />} iconPosition="start" label="Sessão atual" />
        <Tab value="report" icon={<ReportIcon fontSize="small" />} iconPosition="start" label="Relatório por período" />
      </Tabs>

      {tab === 'session' && (loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : !currentRegister ? (
        /* ═══════ NENHUM CAIXA ABERTO ═══════ */
        <Card sx={{ maxWidth: 520, mx: 'auto', mt: 6, textAlign: 'center', p: 4 }}>
          <LockIcon sx={{ fontSize: 64, color: '#9ca3af', mb: 2 }} />
          <Typography variant="h6" gutterBottom>Você não tem caixa aberto</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Abra o seu caixa para começar a registrar entradas, saídas, sangrias e suprimentos.
            Recebimentos feitos por <strong>{user?.full_name || 'você'}</strong> serão lançados automaticamente neste caixa.
          </Typography>
          <Button variant="contained" size="large" startIcon={<LockOpenIcon />}
            onClick={() => setOpenDialogVisible(true)}
            sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}>
            Abrir Caixa
          </Button>
        </Card>
      ) : (
        /* ═══════ CAIXA ABERTO ═══════ */
        <>
          {/* Session info banner */}
          <Alert
            severity="info"
            icon={<WalletIcon />}
            sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.5, sm: 3 }} alignItems={{ sm: 'center' }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Aberto por</Typography>
                <Typography variant="body2" fontWeight={600}>{openedByName}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Início da sessão</Typography>
                <Typography variant="body2" fontWeight={600}>{formatDateTime(sessionStart)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">ID da sessão</Typography>
                <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                  #{(currentRegister.id || '').toString().substring(0, 8)}
                </Typography>
              </Box>
            </Stack>
          </Alert>

          {/* Summary cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: '#eff6ff', border: '1px solid #bfdbfe' }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Saldo de Abertura</Typography>
                  <Typography variant="h6" fontWeight={700}>{formatCurrency(summary!.opening)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDateTime(sessionStart)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: '#ecfdf5', border: '1px solid #a7f3d0' }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Entradas</Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#059669' }}>
                    {formatCurrency(summary!.totalIn)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {movements.filter(m => isPositiveMovement(m.movement_type)).length} lançamentos
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: '#fef2f2', border: '1px solid #fecaca' }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Saídas</Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#dc2626' }}>
                    {formatCurrency(summary!.totalOut)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {movements.filter(m => !isPositiveMovement(m.movement_type)).length} lançamentos
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: '#f5f3ff', border: '1px solid #c4b5fd' }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Saldo Esperado</Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#7c3aed' }}>
                    {formatCurrency(summary!.expectedBalance)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Abertura + entradas − saídas
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Breakdown by payment method */}
          {Object.keys(summary!.byMethod).length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                  Resumo por forma de pagamento
                </Typography>
                <Grid container spacing={2}>
                  {Object.entries(summary!.byMethod)
                    .sort((a, b) => Math.abs(b[1].net) - Math.abs(a[1].net))
                    .map(([method, vals]) => {
                      const total = Math.max(summary!.totalIn, 1);
                      const pct = (vals.in / total) * 100;
                      return (
                        <Grid item xs={12} sm={6} md={4} key={method}>
                          <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e5e7eb' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                              <Typography variant="body2" fontWeight={600}>
                                {paymentMethodLabel(method)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">{vals.count}×</Typography>
                            </Box>
                            <Stack direction="row" spacing={2} alignItems="baseline">
                              <Box>
                                <Typography variant="caption" color="text.secondary" display="block">Entradas</Typography>
                                <Typography variant="body2" fontWeight={600} sx={{ color: '#059669' }}>
                                  {formatCurrency(vals.in)}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary" display="block">Saídas</Typography>
                                <Typography variant="body2" fontWeight={600} sx={{ color: '#dc2626' }}>
                                  {formatCurrency(vals.out)}
                                </Typography>
                              </Box>
                              <Box sx={{ ml: 'auto', textAlign: 'right' }}>
                                <Typography variant="caption" color="text.secondary" display="block">Líquido</Typography>
                                <Typography variant="body2" fontWeight={700}
                                  sx={{ color: vals.net >= 0 ? '#059669' : '#dc2626' }}>
                                  {formatCurrency(vals.net)}
                                </Typography>
                              </Box>
                            </Stack>
                            <LinearProgress variant="determinate" value={Math.min(100, pct)}
                              sx={{ mt: 1, height: 4, borderRadius: 2 }} />
                          </Box>
                        </Grid>
                      );
                    })}
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Action buttons */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 3 }}>
            <Button variant="contained" startIcon={<AddIcon />}
              onClick={() => setMovementDialogVisible(true)}
              sx={{ bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}>
              Nova Movimentação
            </Button>
            <Button variant="outlined" color="error" startIcon={<LockIcon />}
              onClick={() => { setClosingBalance(summary!.expectedBalance); setCloseDialogVisible(true); }}>
              Fechar Caixa
            </Button>
          </Stack>

          {/* Filters */}
          <Grid container spacing={1} sx={{ mb: 1 }}>
            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo</InputLabel>
                <Select value={movFilterType} label="Tipo" onChange={(e) => setMovFilterType(e.target.value)}>
                  <MenuItem value="">Todos</MenuItem>
                  {MOVEMENT_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Forma de pagamento</InputLabel>
                <Select value={movFilterMethod} label="Forma de pagamento"
                  onChange={(e) => setMovFilterMethod(e.target.value)}>
                  <MenuItem value="">Todas</MenuItem>
                  {PAYMENT_METHODS.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={6}>
              <TextField fullWidth size="small" placeholder="Buscar por descrição, paciente ou referência"
                value={movSearch} onChange={(e) => setMovSearch(e.target.value)} />
            </Grid>
          </Grid>

          {/* Movements table */}
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1, mt: 1 }}>
            Movimentações ({filteredMovements.length} de {movements.length})
          </Typography>
          <TableContainer component={Paper} sx={{ borderRadius: 2, mb: 4 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Tipo</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Paciente</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Descrição</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Referência</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Valor</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Método</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Data</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredMovements.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4, color: '#9ca3af' }}>
                      Nenhuma movimentação encontrada
                    </TableCell>
                  </TableRow>
                ) : filteredMovements.map((m, idx) => {
                  const positive = isPositiveMovement(m.movement_type);
                  return (
                    <TableRow key={m.id || idx} hover>
                      <TableCell>
                        <Chip
                          icon={positive ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />}
                          label={MOVEMENT_TYPE_LABELS[m.movement_type] || m.movement_type}
                          size="small"
                          sx={{
                            bgcolor: positive ? '#dcfce7' : '#fee2e2',
                            color: positive ? '#166534' : '#991b1b',
                            fontWeight: 600,
                            '& .MuiChip-icon': { color: positive ? '#166534' : '#991b1b' },
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>
                        <Typography variant="body2" noWrap title={m.patient_name || ''}>
                          {m.patient_name || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>{m.description || '—'}</TableCell>
                      <TableCell>
                        {m.transaction_id ? (
                          <Tooltip title={`Origem: transação ${m.transaction_id.substring(0, 8)}`}>
                            <Chip icon={<ReceiptIcon fontSize="small" />} size="small" label="Auto" color="info" variant="outlined" />
                          </Tooltip>
                        ) : (m.reference || '—')}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: positive ? '#059669' : '#dc2626' }}>
                        {positive ? '+' : '-'} {formatCurrency(m.amount)}
                      </TableCell>
                      <TableCell>{paymentMethodLabel(m.payment_method)}</TableCell>
                      <TableCell>{formatDateTime(m.created_at)}</TableCell>
                      <TableCell align="center">
                        <Tooltip title={m.transaction_id ? 'Movimentos vinculados a transações devem ser ajustados pela origem' : 'Excluir movimento'}>
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteMovementId(m.id)}
                              disabled={!m.id || !!m.transaction_id}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ))}

      {tab === 'session' && (<>
      {/* ═══════ HISTÓRICO ═══════ */}
      <Divider sx={{ my: 3 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <HistoryIcon sx={{ color: '#6b7280' }} />
        <Typography variant="h6" fontWeight={600}>Histórico do meu caixa</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
          (use a aba <strong>Relatório por período</strong> para análises detalhadas)
        </Typography>
      </Box>

      {historyLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700 }}>Abertura</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Fechamento</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Operadores</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Abertura</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Esperado</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Fechamento</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Diferença</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4, color: '#9ca3af' }}>
                    Nenhum registro encontrado
                  </TableCell>
                </TableRow>
              ) : history.map((h) => {
                const diff = h.difference != null ? Number(h.difference) : null;
                const diffColor = diff == null || diff === 0 ? '#374151' : diff > 0 ? '#059669' : '#dc2626';
                return (
                  <TableRow key={h.id} hover>
                    <TableCell>{formatDateTime(h.open_date || h.opened_at || h.created_at)}</TableCell>
                    <TableCell>{formatDateTime(h.close_date || h.closed_at)}</TableCell>
                    <TableCell>
                      <Stack spacing={0.25}>
                        <Typography variant="caption">
                          <UsersIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />
                          {h.opened_by_name || h.opened_by_user?.full_name || '—'}
                        </Typography>
                        {(h.closed_by_name || h.closed_by_user?.full_name) && (
                          <Typography variant="caption" color="text.secondary">
                            Fechou: {h.closed_by_name || h.closed_by_user?.full_name}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">{formatCurrency(h.opening_balance)}</TableCell>
                    <TableCell align="right">
                      {h.expected_balance != null ? formatCurrency(h.expected_balance) : '—'}
                    </TableCell>
                    <TableCell align="right">
                      {h.closing_balance != null ? formatCurrency(h.closing_balance) : '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ color: diffColor, fontWeight: 600 }}>
                      {diff != null ? formatCurrency(diff) : '—'}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={h.status === 'closed' ? 'Fechado' : 'Aberto'}
                        size="small"
                        sx={{
                          bgcolor: h.status === 'closed' ? '#6b7280' : '#10b981',
                          color: '#fff',
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Ver detalhes">
                        <IconButton size="small" onClick={() => handleOpenDetails(h.id)}>
                          <ReceiptIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      </>)}

      {tab === 'report' && (
        <Box>
          {/* Filtros */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1 }}>
                  <CalendarIcon sx={{ color: '#3b82f6' }} />
                  <Typography variant="subtitle2" fontWeight={700}>Filtros</Typography>
                </Box>
                <ButtonGroup size="small" variant="outlined">
                  <Button onClick={() => applyPreset('today')}>Hoje</Button>
                  <Button onClick={() => applyPreset('yesterday')}>Ontem</Button>
                  <Button onClick={() => applyPreset('7d')}>7d</Button>
                  <Button onClick={() => applyPreset('30d')}>30d</Button>
                  <Button onClick={() => applyPreset('month')}>Este mês</Button>
                  <Button onClick={() => applyPreset('last_month')}>Mês passado</Button>
                </ButtonGroup>
              </Stack>
              <Grid container spacing={1.5}>
                <Grid item xs={6} sm={3} md={2}>
                  <TextField fullWidth size="small" type="date" label="De"
                    InputLabelProps={{ shrink: true }} value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)} />
                </Grid>
                <Grid item xs={6} sm={3} md={2}>
                  <TextField fullWidth size="small" type="date" label="Até"
                    InputLabelProps={{ shrink: true }} value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)} />
                </Grid>
                <Grid item xs={6} sm={3} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select value={statusFilter} label="Status"
                      onChange={(e) => setStatusFilter(e.target.value as any)}>
                      <MenuItem value="all">Todos</MenuItem>
                      <MenuItem value="open">Abertos</MenuItem>
                      <MenuItem value="closed">Fechados</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {isAdminMaster && (
                  <Grid item xs={12} sm={3} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Operador</InputLabel>
                      <Select value={userFilter} label="Operador"
                        onChange={(e) => setUserFilter(e.target.value)}>
                        <MenuItem value="all">Todos os operadores</MenuItem>
                        {unitRegistrars.map((u) => (
                          <MenuItem key={u.id} value={u.id}>{u.full_name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={12} md>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                    <Button variant="contained" startIcon={<RefreshIcon />}
                      onClick={fetchReport} disabled={reportLoading}>
                      Atualizar
                    </Button>
                    <Button variant="outlined" startIcon={<PrintIcon />}
                      onClick={printPeriodReport} disabled={!report || reportLoading}>
                      Imprimir
                    </Button>
                    <Button variant="outlined" startIcon={<DownloadIcon />}
                      onClick={exportPeriodCsv} disabled={!report || reportLoading}>
                      Exportar CSV
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {reportError && <Alert severity="error" sx={{ mb: 2 }}>{reportError}</Alert>}

          {reportLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : !report ? (
            <Alert severity="info">Selecione o período e clique em <strong>Atualizar</strong>.</Alert>
          ) : (
            <>
              {/* KPIs */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={4} md={2}>
                  <Card sx={{ bgcolor: '#f8fafc', border: '1px solid #e5e7eb' }}>
                    <CardContent>
                      <Typography variant="caption" color="text.secondary">Caixas</Typography>
                      <Typography variant="h6" fontWeight={700}>{report.totals.registers}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {report.totals.registers_open} aberto(s) · {report.totals.registers_closed} fechado(s)
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Card sx={{ bgcolor: '#eff6ff', border: '1px solid #bfdbfe' }}>
                    <CardContent>
                      <Typography variant="caption" color="text.secondary">Abertura</Typography>
                      <Typography variant="h6" fontWeight={700}>{formatCurrency(report.totals.total_opening)}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Card sx={{ bgcolor: '#ecfdf5', border: '1px solid #a7f3d0' }}>
                    <CardContent>
                      <Typography variant="caption" color="text.secondary">Entradas</Typography>
                      <Typography variant="h6" fontWeight={700} sx={{ color: '#059669' }}>
                        {formatCurrency(report.totals.total_in)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Card sx={{ bgcolor: '#fef2f2', border: '1px solid #fecaca' }}>
                    <CardContent>
                      <Typography variant="caption" color="text.secondary">Saídas</Typography>
                      <Typography variant="h6" fontWeight={700} sx={{ color: '#dc2626' }}>
                        {formatCurrency(report.totals.total_out)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Card sx={{ bgcolor: '#f5f3ff', border: '1px solid #c4b5fd' }}>
                    <CardContent>
                      <Typography variant="caption" color="text.secondary">Saldo líquido</Typography>
                      <Typography variant="h6" fontWeight={700} sx={{ color: '#7c3aed' }}>
                        {formatCurrency(report.totals.total_in - report.totals.total_out)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Esperado: {formatCurrency(report.totals.total_expected)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Card sx={{
                    bgcolor: report.totals.total_difference === 0 ? '#ecfdf5'
                      : report.totals.total_difference > 0 ? '#eff6ff' : '#fef2f2',
                    border: '1px solid #e5e7eb',
                  }}>
                    <CardContent>
                      <Typography variant="caption" color="text.secondary">Diferença</Typography>
                      <Typography variant="h6" fontWeight={700}
                        sx={{ color: report.totals.total_difference === 0 ? '#059669'
                          : report.totals.total_difference > 0 ? '#0369a1' : '#dc2626' }}>
                        {formatCurrency(report.totals.total_difference)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {report.totals.movements_count} mov.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Por método */}
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                    Por forma de pagamento
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                          <TableCell sx={{ fontWeight: 700 }}>Método</TableCell>
                          <TableCell sx={{ fontWeight: 700 }} align="right">Entradas</TableCell>
                          <TableCell sx={{ fontWeight: 700 }} align="right">Saídas</TableCell>
                          <TableCell sx={{ fontWeight: 700 }} align="right">Líquido</TableCell>
                          <TableCell sx={{ fontWeight: 700 }} align="right">Qtd.</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>%</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(report.by_method).length === 0 ? (
                          <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3, color: '#9ca3af' }}>Sem movimentações no período</TableCell></TableRow>
                        ) : Object.entries(report.by_method)
                          .sort((a, b) => Math.abs(b[1].net) - Math.abs(a[1].net))
                          .map(([m, v]) => {
                            const pct = report.totals.total_in > 0 ? (v.in / report.totals.total_in) * 100 : 0;
                            return (
                              <TableRow key={m} hover>
                                <TableCell>{paymentMethodLabel(m)}</TableCell>
                                <TableCell align="right" sx={{ color: '#059669', fontWeight: 600 }}>{formatCurrency(v.in)}</TableCell>
                                <TableCell align="right" sx={{ color: '#dc2626', fontWeight: 600 }}>{formatCurrency(v.out)}</TableCell>
                                <TableCell align="right" sx={{ color: v.net >= 0 ? '#059669' : '#dc2626', fontWeight: 700 }}>{formatCurrency(v.net)}</TableCell>
                                <TableCell align="right">{v.count}</TableCell>
                                <TableCell sx={{ minWidth: 120 }}>
                                  <LinearProgress variant="determinate" value={Math.min(100, pct)}
                                    sx={{ height: 6, borderRadius: 3 }} />
                                  <Typography variant="caption" color="text.secondary">{pct.toFixed(1)}%</Typography>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>

              {/* Por operador (admins ou >1 operador) */}
              {(isAdminMaster || report.by_user.length > 1) && (
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                      Por operador
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f8fafc' }}>
                            <TableCell sx={{ fontWeight: 700 }}>Operador</TableCell>
                            <TableCell sx={{ fontWeight: 700 }} align="right">Sessões</TableCell>
                            <TableCell sx={{ fontWeight: 700 }} align="right">Entradas</TableCell>
                            <TableCell sx={{ fontWeight: 700 }} align="right">Saídas</TableCell>
                            <TableCell sx={{ fontWeight: 700 }} align="right">Líquido</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {report.by_user.length === 0 ? (
                            <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: '#9ca3af' }}>Sem dados</TableCell></TableRow>
                          ) : report.by_user.map((u) => (
                            <TableRow key={u.user_id || u.user_name || 'none'} hover>
                              <TableCell>{u.user_name || '—'}</TableCell>
                              <TableCell align="right">{u.registers}</TableCell>
                              <TableCell align="right" sx={{ color: '#059669' }}>{formatCurrency(u.in)}</TableCell>
                              <TableCell align="right" sx={{ color: '#dc2626' }}>{formatCurrency(u.out)}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, color: u.net >= 0 ? '#059669' : '#dc2626' }}>{formatCurrency(u.net)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              )}

              {/* Sessões do período */}
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1, mt: 1 }}>
                Sessões do período ({report.sessions.length})
              </Typography>
              <TableContainer component={Paper} sx={{ borderRadius: 2, mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      <TableCell sx={{ fontWeight: 700 }}>Abertura</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Fechamento</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Operador</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Abertura</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Entradas</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Saídas</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Esperado</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Fechamento</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Diferença</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="center">Status</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="center">Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {report.sessions.length === 0 ? (
                      <TableRow><TableCell colSpan={11} align="center" sx={{ py: 4, color: '#9ca3af' }}>Nenhuma sessão no período</TableCell></TableRow>
                    ) : report.sessions.map((s) => {
                      const diff = s.difference != null ? Number(s.difference) : null;
                      const diffColor = diff == null || diff === 0 ? '#374151' : diff > 0 ? '#059669' : '#dc2626';
                      return (
                        <TableRow key={s.id} hover>
                          <TableCell>{formatDateTime(s.open_date)}</TableCell>
                          <TableCell>{formatDateTime(s.close_date)}</TableCell>
                          <TableCell>{s.opened_by_name || '—'}</TableCell>
                          <TableCell align="right">{formatCurrency(s.opening_balance)}</TableCell>
                          <TableCell align="right" sx={{ color: '#059669' }}>{formatCurrency(s.total_in)}</TableCell>
                          <TableCell align="right" sx={{ color: '#dc2626' }}>{formatCurrency(s.total_out)}</TableCell>
                          <TableCell align="right">{formatCurrency(s.expected_balance)}</TableCell>
                          <TableCell align="right">{s.closing_balance != null ? formatCurrency(s.closing_balance) : '—'}</TableCell>
                          <TableCell align="right" sx={{ color: diffColor, fontWeight: 600 }}>{diff != null ? formatCurrency(diff) : '—'}</TableCell>
                          <TableCell align="center">
                            <Chip label={s.status === 'closed' ? 'Fechado' : 'Aberto'} size="small"
                              sx={{ bgcolor: s.status === 'closed' ? '#6b7280' : '#10b981', color: '#fff', fontWeight: 600 }} />
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Ver detalhes">
                              <IconButton size="small" onClick={() => handleOpenDetails(s.id)}>
                                <ReceiptIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Box>
      )}

      {/* ═══════ DIALOG: ABRIR CAIXA ═══════ */}
      <Dialog fullScreen={isMobile} scroll="paper" open={openDialogVisible}
        onClose={() => setOpenDialogVisible(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Abrir Caixa
          <IconButton onClick={() => setOpenDialogVisible(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            Operador: <strong>{user?.full_name || '—'}</strong>
          </Alert>
          <TextField
            fullWidth label="Saldo de Abertura" type="number" value={openingBalance}
            onChange={(e) => setOpeningBalance(parseFloat(e.target.value) || 0)}
            InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
            inputProps={{ min: 0, step: 0.01 }}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField fullWidth label="Observações (opcional)" multiline rows={2}
            value={openingNotes} onChange={(e) => setOpeningNotes(e.target.value)} />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenDialogVisible(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleOpenRegister} disabled={openingRegister}
            sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}>
            {openingRegister ? <CircularProgress size={22} /> : 'Abrir'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════ DIALOG: NOVA MOVIMENTAÇÃO ═══════ */}
      <Dialog fullScreen={isMobile} scroll="paper" open={movementDialogVisible}
        onClose={() => setMovementDialogVisible(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Nova Movimentação
          <IconButton onClick={() => setMovementDialogVisible(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo</InputLabel>
                <Select value={movementForm.movement_type} label="Tipo"
                  onChange={(e) => setMovementForm({ ...movementForm, movement_type: e.target.value })}>
                  {MOVEMENT_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.positive ? '↑' : '↓'} {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Valor" type="number" value={movementForm.amount}
                onChange={(e) => setMovementForm({ ...movementForm, amount: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                inputProps={{ min: 0.01, step: 0.01 }} required />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Descrição" value={movementForm.description}
                onChange={(e) => setMovementForm({ ...movementForm, description: e.target.value })}
                required inputProps={{ maxLength: 255 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Método de Pagamento</InputLabel>
                <Select value={movementForm.payment_method} label="Método de Pagamento"
                  onChange={(e) => setMovementForm({ ...movementForm, payment_method: e.target.value })}>
                  {PAYMENT_METHODS.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Referência (opcional)"
                placeholder="Ex: NF 1234, Pedido #567"
                value={movementForm.reference}
                onChange={(e) => setMovementForm({ ...movementForm, reference: e.target.value })}
                inputProps={{ maxLength: 255 }} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setMovementDialogVisible(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleAddMovement} disabled={savingMovement}
            sx={{ bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}>
            {savingMovement ? <CircularProgress size={22} /> : 'Adicionar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════ DIALOG: FECHAR CAIXA ═══════ */}
      <Dialog fullScreen={isMobile} scroll="paper" open={closeDialogVisible}
        onClose={() => setCloseDialogVisible(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Fechar Caixa
          <IconButton onClick={() => setCloseDialogVisible(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Stack spacing={0.25}>
              <Typography variant="caption">
                Abertura: <strong>{formatCurrency(summary?.opening || 0)}</strong>
              </Typography>
              <Typography variant="caption">
                Entradas: <strong>{formatCurrency(summary?.totalIn || 0)}</strong>
              </Typography>
              <Typography variant="caption">
                Saídas: <strong>{formatCurrency(summary?.totalOut || 0)}</strong>
              </Typography>
              <Typography variant="body2">
                Saldo esperado: <strong>{formatCurrency(summary?.expectedBalance || 0)}</strong>
              </Typography>
            </Stack>
          </Alert>
          <TextField
            fullWidth label="Saldo Contado (R$)" type="number" value={closingBalance}
            onChange={(e) => setClosingBalance(parseFloat(e.target.value) || 0)}
            InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
            inputProps={{ min: 0, step: 0.01 }}
            sx={{ mb: 2 }}
            helperText="Informe o valor efetivamente presente no caixa."
          />
          {summary && (() => {
            const diff = closingBalance - summary.expectedBalance;
            const color = diff === 0 ? '#059669' : diff > 0 ? '#0369a1' : '#dc2626';
            return (
              <Alert severity={diff === 0 ? 'success' : diff > 0 ? 'info' : 'warning'} sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ color }}>
                  Diferença: <strong>{formatCurrency(diff)}</strong>
                  {diff === 0 ? ' — valores conferem.'
                    : diff > 0 ? ' — sobrou dinheiro no caixa.'
                    : ' — falta dinheiro no caixa.'}
                </Typography>
              </Alert>
            );
          })()}
          <TextField fullWidth label="Observações do fechamento" multiline rows={3}
            value={closingNotes} onChange={(e) => setClosingNotes(e.target.value)}
            placeholder="Ex: diferença justificada por troco..." />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCloseDialogVisible(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleCloseRegister} disabled={closingRegister}>
            {closingRegister ? <CircularProgress size={22} /> : 'Fechar Caixa'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════ DIALOG: EXCLUIR MOVIMENTO ═══════ */}
      <Dialog open={!!deleteMovementId} onClose={() => setDeleteMovementId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Excluir movimento?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Esta ação é irreversível. O movimento será removido do caixa atual e uma entrada de auditoria será registrada.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteMovementId(null)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleRemoveMovement} disabled={deletingMovement}>
            {deletingMovement ? <CircularProgress size={22} /> : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════ DIALOG: DETALHES HISTÓRICO ═══════ */}
      <Dialog fullScreen={isMobile} open={!!detailRegisterId} onClose={closeDetailDialog}
        maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Detalhes do Caixa
          <Stack direction="row" spacing={0.5}>
            {detailRegister && (
              <Tooltip title="Imprimir">
                <IconButton onClick={() => printRegister(detailRegister)}><PrintIcon /></IconButton>
              </Tooltip>
            )}
            <IconButton onClick={closeDetailDialog}><CloseIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : !detailRegister ? (
            <Alert severity="error">Não foi possível carregar os detalhes.</Alert>
          ) : (
            <>
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary">Abertura</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatDateTime(detailRegister.open_date)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary">Fechamento</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatDateTime(detailRegister.close_date)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Chip size="small" label={detailRegister.status === 'closed' ? 'Fechado' : 'Aberto'}
                    sx={{ bgcolor: detailRegister.status === 'closed' ? '#6b7280' : '#10b981', color: '#fff', fontWeight: 600 }} />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary">Aberto por</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {detailRegister.opened_by_user?.full_name || '—'}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary">Fechado por</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {detailRegister.closed_by_user?.full_name || '—'}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary">Movimentações</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {(detailRegister.movements || []).length}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Abertura</Typography>
                  <Typography variant="body2" fontWeight={700}>{formatCurrency(detailRegister.opening_balance)}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Entradas</Typography>
                  <Typography variant="body2" fontWeight={700} sx={{ color: '#059669' }}>
                    {formatCurrency(detailRegister.total_in)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Saídas</Typography>
                  <Typography variant="body2" fontWeight={700} sx={{ color: '#dc2626' }}>
                    {formatCurrency(detailRegister.total_out)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    {detailRegister.status === 'closed' ? 'Fechamento' : 'Esperado'}
                  </Typography>
                  <Typography variant="body2" fontWeight={700} sx={{ color: '#7c3aed' }}>
                    {formatCurrency(detailRegister.closing_balance ?? detailRegister.expected_balance)}
                  </Typography>
                  {detailRegister.difference != null && (
                    <Typography variant="caption"
                      sx={{ color: Number(detailRegister.difference) === 0 ? '#059669'
                        : Number(detailRegister.difference) > 0 ? '#0369a1' : '#dc2626' }}>
                      Diferença: {formatCurrency(detailRegister.difference)}
                    </Typography>
                  )}
                </Grid>
              </Grid>
              {detailRegister.notes && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block">Observações</Typography>
                  {detailRegister.notes}
                </Alert>
              )}
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Movimentações</Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      <TableCell>Tipo</TableCell>
                      <TableCell>Paciente</TableCell>
                      <TableCell>Descrição</TableCell>
                      <TableCell>Método</TableCell>
                      <TableCell align="right">Valor</TableCell>
                      <TableCell>Data</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detailRegister.movements || []).length === 0 ? (
                      <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3, color: '#9ca3af' }}>Sem movimentações</TableCell></TableRow>
                    ) : detailRegister.movements.map((m: any) => {
                      const positive = isPositiveMovement(m.movement_type);
                      return (
                        <TableRow key={m.id}>
                          <TableCell>{MOVEMENT_TYPE_LABELS[m.movement_type] || m.movement_type}</TableCell>
                          <TableCell>{m.patient_name || '—'}</TableCell>
                          <TableCell>{m.description || '—'}</TableCell>
                          <TableCell>{paymentMethodLabel(m.payment_method)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: positive ? '#059669' : '#dc2626' }}>
                            {positive ? '+' : '-'} {formatCurrency(m.amount)}
                          </TableCell>
                          <TableCell>{formatDate(m.created_at)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ═══════ SNACKBAR ═══════ */}
      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}>
        <Alert severity={snackbar.severity} variant="filled"
          onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CashRegister;
